import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import type { StockTransferStatus } from "@/modules/inventory/presentation/api/client";
import { TRANSFER_STATUS_LABELS } from "@/modules/inventory/presentation/ui/transfers/TransferStatusBadge";
import {
  formatDateForInput,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { TRANSFER_LIST_FILTER } from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import { XIcon } from "lucide-react";

const FILTER_ALL = "__all__" as const;

const STATUS_FILTER_ORDER: StockTransferStatus[] = [
  "SUBMITTED",
  "APPROVED",
  "SHIPPED",
  "RECEIVED",
  "CLOSED",
  "DRAFT",
  "REJECTED",
  "CANCELLED",
];

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

type TransfersSearch = {
  status?: StockTransferStatus;
  fromBranchId?: string;
  toBranchId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  offset?: number;
  limit?: number;
};

export function TransfersFilter() {
  const nav = useNavigate({ from: "/app/inventory/transfers" });
  const search = useSearch({ from: "/app/inventory/transfers" }) as TransfersSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });

  const branches: BranchRow[] = branchesData?.data ?? [];

  const statusOptions = [
    { value: FILTER_ALL, label: "ທຸກສະຖານະ" },
    ...STATUS_FILTER_ORDER.map((s) => ({
      value: s,
      label: TRANSFER_STATUS_LABELS[s],
    })),
  ];

  const branchOptions = [
    { value: FILTER_ALL, label: "ທຸກສາຂາ" },
    ...branches.map((b) => ({
      value: b.id,
      label: `${b.code} — ${b.name}`,
    })),
  ];

  const hasFilter =
    !!search.status ||
    !!search.fromBranchId ||
    !!search.submittedFrom ||
    !!search.submittedTo;

  const update = (next: Partial<TransfersSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    nav({
      search: {
        limit: search.limit ?? 20,
        offset: 0,
      },
    });
  };

  return (
    <div className={`${TRANSFER_LIST_FILTER} flex flex-wrap items-center gap-2`}>
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={search.status ?? FILTER_ALL}
        onValueChange={(v) => {
          const id = toParam(v ?? "");
          update({
            status: id ? (id as StockTransferStatus) : undefined,
          });
        }}
        options={statusOptions}
        className="h-9 w-44"
      />
      <SimpleSelect
        placeholder="ສາຂາຂໍ"
        value={search.fromBranchId ?? FILTER_ALL}
        onValueChange={(v) => update({ fromBranchId: toParam(v ?? "") })}
        options={branchOptions}
        className="h-9 w-44"
        contentClassName="max-w-[min(100vw-2rem,20rem)]"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.submittedFrom
            ? parseCalendarDateFromInput(search.submittedFrom)
            : undefined,
          to: search.submittedTo
            ? parseCalendarDateFromInput(search.submittedTo)
            : undefined,
        }}
        onChange={(range) => {
          const from = range?.from;
          const to = range?.to;
          update({
            submittedFrom: from ? formatDateForInput(from) : undefined,
            submittedTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນທີສົ່ງຄຳຂໍ"
        className="h-9 w-fit min-w-56"
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex h-9 items-center gap-1 px-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
